/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AddressInputProps {
  value: string;
  onChange: (value: string, coords?: { lat: number; lng: number }) => void;
}

const AddressInput = ({ value, onChange }: AddressInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!apiKey || !inputRef.current) return;

    const loader = new Loader({
      apiKey,
      version: "weekly",
      libraries: ["places"],
    });

    loader
      .load()
      .then(() => {
        if (!inputRef.current) return;
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ["formatted_address", "geometry"],
        });
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current?.getPlace();
          if (!place) return;
          const address = place.formatted_address || inputRef.current?.value || "";
          const loc = place.geometry?.location;
          if (loc) {
            onChange(address, { lat: loc.lat(), lng: loc.lng() });
          } else {
            onChange(address);
          }
        });
        setReady(true);
      })
      .catch((err) => {
        console.error("Failed to load Google Places Autocomplete:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return (
    <div>
      <Label htmlFor="address">Address</Label>
      <Input
        id="address"
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={ready ? "Start typing your address..." : "Enter address"}
        autoComplete="off"
      />
    </div>
  );
};

export default AddressInput;
